import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  resumeAudio, playLogoChime, playPop, playTwinkle,
  playRainbowWhoosh, playSunshine, playShapeBounce, playSubtitleReveal
} from '../../utils/introSounds';

const LOGO_URL = '/logo_transparent.png';

const Block3D = ({ char, color, shadow, delay, endX, endY, size = 40 }) => (
  <motion.div
    initial={{ opacity: 0, x: 0, y: 0, scale: 0, rotate: -20 }}
    animate={{
      opacity: [0, 0.8, 1, 1],
      x: [0, endX * 0.3, endX * 0.7, endX],
      y: [0, endY * 0.3, endY * 0.7, endY],
      scale: [0, 0.4, 0.8, 1],
      rotate: [-20, 5, -3, 0],
    }}
    transition={{ delay, duration: 2.5, ease: [0.25, 0.46, 0.45, 0.94] }}
    className="absolute font-bold text-white flex items-center justify-center rounded-lg"
    style={{
      left: '50%', top: '50%',
      marginLeft: -size / 2, marginTop: -size / 2,
      width: size, height: size,
      background: `linear-gradient(145deg, ${color}, ${shadow})`,
      boxShadow: `3px 3px 0px ${shadow}, 4px 4px 8px rgba(0,0,0,0.12)`,
      fontSize: size * 0.5,
      fontFamily: "'Fredoka One', 'Nunito', sans-serif",
      zIndex: 10,
    }}
  >
    <motion.span animate={{ y: [0, -3, 0] }} transition={{ duration: 2.5, repeat: Infinity, delay: delay + 2, ease: 'easeInOut' }}>
      {char}
    </motion.span>
  </motion.div>
);

const FlyingShape = ({ type, color, delay, endX, endY, size = 24 }) => {
  const shapes = {
    circle: <circle cx="20" cy="20" r="18" fill={color} opacity="0.7" />,
    triangle: <polygon points="20,2 38,38 2,38" fill={color} opacity="0.7" />,
    star: <path d="M20 2L24.5 14.5L38 14.5L27 22.5L30.5 36L20 28L9.5 36L13 22.5L2 14.5L15.5 14.5Z" fill={color} opacity="0.7" />,
    diamond: <polygon points="20,2 36,20 20,38 4,20" fill={color} opacity="0.7" />,
    heart: <path d="M20 35 C20 35 4 24 4 14 C4 8 8 4 14 4 C17 4 19 6 20 8 C21 6 23 4 26 4 C32 4 36 8 36 14 C36 24 20 35 20 35Z" fill={color} opacity="0.65" />,
  };
  return (
    <motion.div
      initial={{ opacity: 0, x: 0, y: 0, scale: 0 }}
      animate={{ opacity: [0, 0.6, 0.8, 0.7], x: [0, endX * 0.3, endX * 0.65, endX], y: [0, endY * 0.3, endY * 0.65, endY], scale: [0, 0.3, 0.7, 1] }}
      transition={{ delay, duration: 2.8, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="absolute" style={{ left: '50%', top: '50%', marginLeft: -size / 2, marginTop: -size / 2, zIndex: 8 }}
    >
      <motion.svg width={size} height={size} viewBox="0 0 40 40" animate={{ rotate: [0, 360] }} transition={{ duration: 12, repeat: Infinity, ease: 'linear', delay: delay + 2 }}>
        {shapes[type]}
      </motion.svg>
    </motion.div>
  );
};

const Sparkle = ({ delay, x, y, size = 14 }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0 }}
    animate={{ opacity: [0, 1, 0.6, 1, 0], scale: [0, 1.2, 0.8, 1, 0], rotate: [0, 90, 180, 270, 360] }}
    transition={{ delay, duration: 2.5, ease: 'easeInOut' }}
    className="absolute" style={{ left: x, top: y }}
  >
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M12 0L14.5 9.5L24 12L14.5 14.5L12 24L9.5 14.5L0 12L9.5 9.5L12 0Z" fill="#FFD93D" />
    </svg>
  </motion.div>
);

const MobileIntro = ({ onComplete }) => {
  const [phase, setPhase] = useState('enter');
  const [soundStarted, setSoundStarted] = useState(false);

  const startSounds = useCallback(() => {
    if (soundStarted) return;
    setSoundStarted(true);
    resumeAudio();
    setTimeout(() => playLogoChime(), 400);
    setTimeout(() => playRainbowWhoosh(), 800);
    setTimeout(() => playSunshine(), 900);
    [1.0, 1.2, 1.4, 1.6, 1.8, 2.0, 2.2, 2.4].forEach((d, i) => {
      setTimeout(() => playPop([1.2, 1.1, 1.0, 0.9, 1.15, 1.05, 0.95, 0.85][i]), d * 1000);
    });
    [1.1, 1.3, 1.5, 1.7, 1.9, 2.1, 2.3, 2.5, 1.5, 1.7].forEach((d, i) => {
      setTimeout(() => playShapeBounce([1.3, 1.1, 0.9, 1.2, 1.0, 0.8, 1.15, 0.95, 1.25, 1.05][i]), d * 1000);
    });
    [2.0, 2.3, 2.6, 2.9, 3.1].forEach(d => setTimeout(() => playTwinkle(), d * 1000));
    setTimeout(() => playSubtitleReveal(), 2800);
  }, [soundStarted]);

  useEffect(() => {
    const handleInteraction = () => { startSounds(); window.removeEventListener('click', handleInteraction); window.removeEventListener('touchstart', handleInteraction); };
    setTimeout(() => startSounds(), 100);
    window.addEventListener('click', handleInteraction);
    window.addEventListener('touchstart', handleInteraction);
    const t1 = setTimeout(() => setPhase('reveal'), 600);
    const t2 = setTimeout(() => setPhase('exit'), 5500);
    const t3 = setTimeout(() => onComplete(), 6500);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); window.removeEventListener('click', handleInteraction); window.removeEventListener('touchstart', handleInteraction); };
  }, [onComplete, startSounds]);

  const blocks = [
    { char: 'A', color: '#F28B82', shadow: '#c96b65', endX: -140, endY: -90, delay: 1.0 },
    { char: 'B', color: '#81C9E8', shadow: '#5fa8c9', endX: 135, endY: -85, delay: 1.2 },
    { char: 'C', color: '#A8D88E', shadow: '#7fb86b', endX: -120, endY: 80, delay: 1.4 },
    { char: '1', color: '#F5C869', shadow: '#d4a84a', endX: 125, endY: 75, delay: 1.6 },
    { char: '2', color: '#C5A3F1', shadow: '#a07fd4', endX: -155, endY: 8, delay: 1.8 },
    { char: '3', color: '#FF9F7F', shadow: '#d97a5c', endX: 150, endY: -3, delay: 2.0 },
    { char: 'D', color: '#88DFCF', shadow: '#5cb8a7', endX: -80, endY: -110, delay: 2.2, size: 34 },
    { char: '4', color: '#FFB3C6', shadow: '#d98fa3', endX: 80, endY: 100, delay: 2.4, size: 34 },
  ];

  const shapes = [
    { type: 'circle', color: '#FFB3B3', endX: -100, endY: -60, delay: 1.1 },
    { type: 'triangle', color: '#A0D4F1', endX: 110, endY: -55, delay: 1.3 },
    { type: 'star', color: '#FFE88D', endX: -85, endY: 65, delay: 1.5 },
    { type: 'diamond', color: '#C5A3F1', endX: 95, endY: 55, delay: 1.7 },
    { type: 'heart', color: '#FFB3C6', endX: -160, endY: -40, delay: 1.9 },
    { type: 'circle', color: '#A8E6A1', endX: 165, endY: 40, delay: 2.1 },
  ];

  return (
    <AnimatePresence>
      {phase !== 'done' && (
        <motion.div
          initial={{ opacity: 1 }} animate={{ opacity: phase === 'exit' ? 0 : 1 }} transition={{ duration: 0.8 }}
          className="fixed inset-0 z-[100] overflow-hidden flex items-center justify-center"
          style={{ background: 'linear-gradient(180deg, #D6EEFF 0%, #E8F4FD 40%, #F0F8FF 70%, #FFF9F0 100%)' }}
          data-testid="mobile-intro"
        >
          {/* Rainbow */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: phase === 'exit' ? 0 : [0, 0.7, 0.6] }} transition={{ delay: 0.8, duration: 1.5 }}
            className="absolute z-10" style={{ width: '95vw', maxWidth: 420, aspectRatio: '2/1', marginTop: '-40%' }}>
            <svg viewBox="0 0 800 400" fill="none" className="w-full h-full overflow-visible">
              <defs><clipPath id="m-rainbow"><motion.rect initial={{ width: 0 }} animate={{ width: 800 }} transition={{ delay: 0.8, duration: 2.5, ease: [0.25, 0.46, 0.45, 0.94] }} x="0" y="0" height="400" /></clipPath></defs>
              <g clipPath="url(#m-rainbow)">
                <path d="M30 390 Q400 -80 770 390" stroke="#FF6B6B" strokeWidth="20" strokeLinecap="round" fill="none" opacity="0.5" />
                <path d="M55 390 Q400 -55 745 390" stroke="#FF9F43" strokeWidth="18" strokeLinecap="round" fill="none" opacity="0.5" />
                <path d="M80 390 Q400 -30 720 390" stroke="#FECA57" strokeWidth="16" strokeLinecap="round" fill="none" opacity="0.5" />
                <path d="M105 390 Q400 -5 695 390" stroke="#48DBFB" strokeWidth="14" strokeLinecap="round" fill="none" opacity="0.45" />
                <path d="M130 390 Q400 20 670 390" stroke="#A29BFE" strokeWidth="10" strokeLinecap="round" fill="none" opacity="0.35" />
              </g>
            </svg>
          </motion.div>

          {/* Sunshine */}
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0 }}
            animate={{ opacity: phase === 'exit' ? 0 : [0, 0.85, 0.75], y: phase === 'exit' ? -40 : [100, -8, 0], scale: phase === 'exit' ? 2 : [0, 1.2, 1] }}
            transition={{ delay: 0.8, duration: 2.5, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="absolute top-[5%] right-[5%] z-10"
          >
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 25, repeat: Infinity, ease: 'linear' }} className="w-16 h-16">
              <svg viewBox="0 0 120 120" fill="none" className="w-full h-full">
                {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map(a => (
                  <line key={a} x1="60" y1="60" x2={60 + 52 * Math.cos(a * Math.PI / 180)} y2={60 + 52 * Math.sin(a * Math.PI / 180)} stroke="#FFD93D" strokeWidth="4" strokeLinecap="round" opacity="0.6" />
                ))}
                <circle cx="60" cy="60" r="22" fill="#FFD93D" opacity="0.8" />
              </svg>
            </motion.div>
          </motion.div>

          {shapes.map((s, i) => <FlyingShape key={i} {...s} />)}
          {blocks.map(b => <Block3D key={b.char + b.endX} {...b} />)}

          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.2, y: 30 }}
            animate={{ opacity: phase === 'exit' ? 0 : 1, scale: phase === 'exit' ? 1.15 : [0.2, 1.08, 0.96, 1], y: phase === 'exit' ? -10 : [30, -5, 3, 0] }}
            transition={{ delay: phase === 'exit' ? 0 : 0.4, duration: phase === 'exit' ? 0.6 : 1.2, ease: phase === 'exit' ? 'easeIn' : [0.34, 1.56, 0.64, 1] }}
            className="relative z-20 px-4"
          >
            <motion.img src={LOGO_URL} alt="Hooyo's Place" className="w-[85vw] max-w-[360px] h-auto" style={{ filter: 'drop-shadow(0 6px 20px rgba(0,0,0,0.08))' }}
              animate={phase !== 'exit' ? { y: [0, -4, 0] } : {}} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 2.5 }} data-testid="mobile-intro-logo" />
          </motion.div>

          <Sparkle delay={2.0} x="15%" y="30%" />
          <Sparkle delay={2.3} x="82%" y="28%" />
          <Sparkle delay={2.6} x="20%" y="65%" />
          <Sparkle delay={2.9} x="78%" y="62%" />

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: phase === 'exit' ? 0 : [0, 0, 1], y: phase === 'exit' ? 8 : [12, 12, 0] }}
            transition={{ delay: 2.8, duration: 0.8, ease: 'easeOut' }}
            className="absolute bottom-[18%] text-center font-nunito font-semibold text-sm tracking-wide z-20" style={{ color: 'hsl(215, 45%, 30%)' }}
            data-testid="mobile-intro-subtitle"
          >Where Little Minds Grow</motion.p>

          {/* Skip */}
          <motion.button
            initial={{ opacity: 0 }} animate={{ opacity: 0.7 }} whileHover={{ opacity: 1 }}
            transition={{ delay: 1.5 }} onClick={onComplete}
            className="absolute bottom-6 left-6 bg-white/90 backdrop-blur-sm text-foreground px-4 py-2 rounded-full text-xs font-bold z-40 shadow-lg border border-black/5"
            data-testid="mobile-intro-skip"
          >Skip</motion.button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MobileIntro;
